/**
 * Seasonal Grams — Free Gift With Purchase (variant-picker modal)
 *
 * A companion app's discount function discounts a gift product to 100% off once it's in the
 * cart and the campaign threshold is met. A function can't ADD to the cart — and we now let the
 * customer choose which variant they want. So instead of auto-adding the first variant, this
 * controller shows a prompt in the cart when a campaign qualifies; the customer opens a modal
 * (see Task 5) and picks a variant, which we add tagged with _sg_free_gift. It still auto-REMOVES
 * the gift when the cart drops below threshold or the campaign ends.
 *
 * Config + UI copy/colors are injected by snippets/free-gift.liquid as window.SeasonalGramsFreeGift.
 * Design: docs/superpowers/specs/2026-06-12-free-gift-variant-modal-design.md
 */
(function () {
  'use strict';

  var config = window.SeasonalGramsFreeGift || {};
  var campaigns = Array.isArray(config.campaigns) ? config.campaigns : [];
  var ui = config.ui || {};
  var popup = ui.popup || {};
  var giftImages = config.giftImages || {};
  var giftPrices = config.giftPrices || {};   // discountId -> gift product price (cents, base currency)
  var showValue = ui.showValue === true;       // slashed "was" price + FREE on every surface

  // No campaigns configured (or metafield absent) → nothing to do.
  if (!campaigns.length) {
    return;
  }

  var PROP = '_sg_free_gift';   // private line-item property tagging our own adds
  var DEBOUNCE_MS = 300;

  // --- small helpers ---------------------------------------------------------

  // Numeric tail of a GraphQL GID: ".../ProductVariant/123" -> "123"
  function gidTail(gid) {
    if (!gid) return null;
    var parts = String(gid).split('/');
    return parts[parts.length - 1] || null;
  }

  function currencyRate() {
    return (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) || 1
      : 1;
  }

  // enabled AND now within [startsAt, endsAt] (endsAt null = open-ended)
  function isEligible(campaign, now) {
    if (!campaign || campaign.enabled !== true) return false;
    var start = campaign.startsAt ? Date.parse(campaign.startsAt) : NaN;
    if (isNaN(start) || now < start) return false;
    if (campaign.endsAt) {
      var end = Date.parse(campaign.endsAt);
      if (!isNaN(end) && now > end) return false;
    }
    return true;
  }

  // The campaign's gift product object (handle + optional featuredImage), or null.
  function giftProduct(campaign) {
    var products = campaign && campaign.giftProducts;
    if (!Array.isArray(products) || !products.length) return null;
    return products[0] || null;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }

  // Slashed "was" price + FREE, shared by the prompt and popup. `cents` is in the shop's base
  // currency; scale to presentment via currencyRate() to match the controller's other money.
  // formatMoneySafe is a hoisted function declaration (defined in the popup section below).
  function valueMarkup(cents) {
    return '<span class="sg-fg-value">' +
      '<s class="sg-fg-value__was">' + formatMoneySafe(cents * currencyRate()) + '</s> ' +
      '<span class="sg-fg-value__free">FREE</span>' +
      '</span>';
  }

  // --- cart I/O --------------------------------------------------------------

  function getCart() {
    return fetch(theme.routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function findGiftLine(cart, discountId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (props[PROP] != null && String(props[PROP]) === String(discountId)) {
        return items[i];
      }
    }
    return null;
  }

  // Cart total after all discounts, minus every tagged gift line.
  function qualifyingTotal(cart) {
    var total = (cart && cart.total_price) || 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (props[PROP] != null) {
        total -= (items[i].final_line_price || 0);
      }
    }
    return total;
  }

  function removeLine(lineKey) {
    return fetch(theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.ok; })
      .catch(function (e) {
        console.warn('[sg-free-gift] remove error', e);
        return false;
      });
  }

  // --- decision --------------------------------------------------------------

  // From one cart snapshot, decide removes (cart writes), promptable campaigns (qualified,
  // DOM only), and approaching campaigns (close to threshold, popup only).
  function plan(cart, now) {
    var removes = [];
    var promptable = [];
    var approaching = [];

    var awayDollars = Number(popup.thresholdAway) || 0;
    var awayCents = awayDollars * 100 * currencyRate();

    for (var i = 0; i < campaigns.length; i++) {
      var c = campaigns[i];
      if (!c || c.discountId == null) continue;

      var giftLine = findGiftLine(cart, c.discountId);
      var eligible = isEligible(c, now);

      if (!eligible) {
        if (giftLine) removes.push(giftLine.key);   // campaign ended → pull our gift
        continue;
      }

      var product = giftProduct(c);
      if (!product || !product.handle) {
        console.warn('[sg-free-gift] campaign ' + c.discountId + ' has no usable gift product; skipping');
        continue;
      }

      var qt = qualifyingTotal(cart);
      var threshold = (Number(c.thresholdSubtotal) || 0) * 100 * currencyRate();

      if (qt >= threshold) {
        if (!giftLine) promptable.push(c);           // qualified, not yet chosen → prompt
      } else {
        if (giftLine) removes.push(giftLine.key);     // below threshold → pull our gift
        // Within the merchant's "$X away" band (and no gift line) → eligible to nudge.
        if (awayCents > 0 && !giftLine && (threshold - qt) <= awayCents) {
          approaching.push({ campaign: c, remainingCents: threshold - qt });
        }
      }
    }
    return { removes: removes, promptable: promptable, approaching: approaching };
  }

  // Serialize removes; resolve to whether anything changed.
  function runRemoves(removes) {
    if (!removes.length) return Promise.resolve(false);
    return removes.reduce(function (chain, key) {
      return chain.then(function () { return removeLine(key); });
    }, Promise.resolve()).then(function () { return true; });
  }

  // One-at-a-time mode: remove every tagged gift line that ISN'T the one we just
  // claimed. Resolves to whether anything was removed.
  function evictOtherGifts(keepDiscountId) {
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var removes = [];
      for (var i = 0; i < items.length; i++) {
        var props = items[i].properties || {};
        if (props[PROP] != null && String(props[PROP]) !== String(keepDiscountId)) {
          removes.push(items[i].key);
        }
      }
      return runRemoves(removes);
    });
  }


  // --- reconcile orchestration (mutex + dirty flag) --------------------------

  var running = false;
  var dirty = false;
  var pendingAdd = false;   // set by cart:item-added so reconcile knows an add happened

  function reconcile() {
    if (running) { dirty = true; return; }
    running = true;
    dirty = false;
    var byAdd = pendingAdd;
    pendingAdd = false;

    getCart()
      .then(function (cart) {
        var p = plan(cart, Date.now());
        maybeShowPopup(p.approaching, byAdd);
        return runRemoves(p.removes);
      })
      .then(function (changed) {
        if (changed) dispatchCustomEvent('cart:refresh');
      })
      .catch(function (e) { console.warn('[sg-free-gift] reconcile error', e); })
      .finally(function () {
        running = false;
        if (dirty) reconcile();
      });
  }

  var onCartEvent = (typeof debounce === 'function') ? debounce(reconcile, DEBOUNCE_MS) : reconcile;

  // --- variant modal ---------------------------------------------------------

  var drawer = null, drawerContent = null, errorEl = null, confirmBtn = null;
  var activeDiscountId = null;
  var activeVariants = [];
  var activeVariantImages = {};   // variant id → image URL, for swapping the picker image

  function modalEls() {
    if (!drawer) drawer = document.getElementById('Free-Gift-Drawer');
    if (!drawerContent) drawerContent = document.getElementById('Free-Gift-Drawer-Content');
    if (!errorEl) errorEl = document.getElementById('Free-Gift-Drawer-Error');
    if (!confirmBtn) confirmBtn = document.getElementById('Free-Gift-Confirm');
    return drawer && drawerContent && confirmBtn;
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  // Selected option values per fieldset, in option order (mirrors the theme's updateOptions).
  function readSelectedOptions() {
    var options = [];
    var fieldsets = drawerContent.querySelectorAll('fieldset[data-handle]');
    for (var i = 0; i < fieldsets.length; i++) {
      var sel = fieldsets[i].querySelector('select');
      if (sel) { options.push(sel.value); continue; }
      var checked = fieldsets[i].querySelector('input:checked');
      if (checked) options.push(checked.value);
    }
    return options;
  }

  // Match selected options against the variants JSON (mirrors the theme's updateMasterId).
  function resolveVariant() {
    if (!activeVariants.length) return null;
    if (activeVariants.length === 1) return activeVariants[0];   // default-variant product
    var options = readSelectedOptions();
    if (!options.length) return null;
    for (var i = 0; i < activeVariants.length; i++) {
      var v = activeVariants[i];
      if (!v.options || v.options.length !== options.length) continue;
      var match = true;
      for (var j = 0; j < options.length; j++) {
        if (String(v.options[j]) !== String(options[j])) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  // Write the currently-resolved variant's price into the modal's slashed "was" element.
  // No-op unless the merchant enabled the value display; hides the strike when no variant resolves.
  function updateModalValue() {
    if (!showValue || !drawerContent) return;
    var wasEl = drawerContent.querySelector('[data-sg-fg-was]');
    if (!wasEl) return;
    var variant = resolveVariant();
    if (variant && typeof variant.price === 'number') {
      wasEl.textContent = formatMoneySafe(variant.price * currencyRate());
      wasEl.hidden = false;
    } else {
      wasEl.textContent = '';
      wasEl.hidden = true;
    }
  }

  // Swap the picker image to the resolved variant's image, falling back to the product image
  // when a variant has no assigned media. Mirrors updateModalValue; map is variant id → URL.
  function updateModalImage() {
    if (!drawerContent) return;
    var img = drawerContent.querySelector('[data-sg-fg-image]');
    if (!img) return;
    var variant = resolveVariant();
    var url = (variant && activeVariantImages) ? activeVariantImages[variant.id] : null;
    if (!url) url = img.getAttribute('data-sg-fg-default-src');
    if (url && img.getAttribute('src') !== url) img.src = url;
  }

  // Sync each option's "Name: <value>" label to its current selection. On the real PDP the
  // <variant-selects> element does this; the picker omits it, so the controller mirrors it here.
  function updateModalLabels() {
    if (!drawerContent) return;
    var fieldsets = drawerContent.querySelectorAll('fieldset[data-handle]');
    for (var i = 0; i < fieldsets.length; i++) {
      var valueEl = fieldsets[i].querySelector('.form__label__value');
      if (!valueEl) continue;
      var sel = fieldsets[i].querySelector('select');
      var checked = sel ? null : fieldsets[i].querySelector('input:checked');
      var current = sel ? sel.value : (checked ? checked.value : null);
      if (current != null) valueEl.textContent = current;
    }
  }

  function openModal(handle, discountId) {
    if (!handle || !modalEls()) return;
    activeDiscountId = discountId;
    activeVariants = [];
    activeVariantImages = {};
    clearError();
    drawerContent.innerHTML = '<div class="sg-fg-loading">Loading…</div>';

    // Don't stack side panels — close any open cart drawer and the "almost there" popup first.
    var cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer) cartDrawer.classList.remove('active');
    closePopup();

    document.body.classList.add('open-cc');
    drawer.classList.add('active');

    var url = theme.routes.root_url.replace(/\/$/, '') + '/products/' +
      encodeURIComponent(handle) + '?view=' + (ui.viewSuffix || 'free-gift');

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.getElementById('Free-Gift-Picker-Content');
        if (!content) {
          drawerContent.innerHTML = '';
          showError('Sorry, this gift is unavailable right now.');
          return;
        }
        var json = content.querySelector('#sg-fg-variants-json');
        try { activeVariants = json ? JSON.parse(json.textContent) : []; }
        catch (e) { activeVariants = []; }
        var imgJson = content.querySelector('#sg-fg-variant-images-json');
        try { activeVariantImages = imgJson ? JSON.parse(imgJson.textContent) : {}; }
        catch (e) { activeVariantImages = {}; }
        drawerContent.innerHTML = content.innerHTML;
        updateModalValue();   // fill the slashed price for the pre-selected variant
        updateModalImage();   // show the pre-selected variant's image
        updateModalLabels();  // sync each option label to the pre-selected variant
        var close = drawer.querySelector('.side-panel-close');
        if (close) close.focus();
      })
      .catch(function (e) {
        console.warn('[sg-free-gift] modal load error', e);
        drawerContent.innerHTML = '';
        showError('Sorry, this gift is unavailable right now.');
      });
  }

  function closeModal() {
    if (!drawer) return;
    drawer.classList.remove('active');
    // Keep the scroll lock if the "almost there" popup is still open (shared open-cc class).
    if (!popupOpen) document.body.classList.remove('open-cc');
  }

  function addGift(discountId, variantId) {
    var props = {};
    props[PROP] = String(discountId);
    return fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: props })
    }).then(function (r) {
      if (!r.ok) {
        showError('Sorry, that option just sold out.');
        return false;
      }
      return true;
    }).catch(function (e) {
      console.warn('[sg-free-gift] add error', e);
      showError('Something went wrong. Please try again.');
      return false;
    });
  }

  function onConfirm() {
    clearError();
    var variant = resolveVariant();
    if (!variant) { showError('Please choose an option.'); return; }
    if (variant.available === false) { showError('That option is sold out.'); return; }
    confirmBtn.disabled = true;
    addGift(activeDiscountId, variant.id).then(function (ok) {
      confirmBtn.disabled = false;
      if (!ok) return;
      // Only one free gift at a time — add first, then evict any other gift line so a
      // sold-out new pick never strands the customer with no gift.
      evictOtherGifts(activeDiscountId).then(function () {
        closeModal();
        dispatchCustomEvent('cart:refresh');
      });
    });
  }

  // --- "almost there" popup --------------------------------------------------

  var popupEl = null, popupImage = null, popupEyebrow = null, popupHeading = null,
      popupValue = null, popupMessage = null, popupCta = null;
  var popupOpen = false;

  function popupEls() {
    if (!popupEl) {
      popupEl = document.getElementById('Free-Gift-Popup');
      popupImage = document.getElementById('Free-Gift-Popup-Image');
      popupEyebrow = document.getElementById('Free-Gift-Popup-Eyebrow');
      popupHeading = document.getElementById('Free-Gift-Popup-Heading');
      popupTimer = document.getElementById('Free-Gift-Popup-Timer');
      popupValue = document.getElementById('Free-Gift-Popup-Value');
      popupMessage = document.getElementById('Free-Gift-Popup-Message');
      popupCta = document.getElementById('Free-Gift-Popup-Cta');
    }
    return popupEl && popupHeading && popupMessage && popupCta;
  }

  function formatMoneySafe(cents) {
    var fmt = (window.theme && theme.settings && theme.settings.money_with_currency_format) || '${{amount}}';
    if (typeof formatMoney === 'function') return formatMoney(cents, fmt);
    return '$' + (cents / 100).toFixed(2);
  }

  var popupTimer = null;   // the <sg-countdown> element; ticking handled by the component itself

  function populatePopup(entry) {
    var c = entry.campaign;
    var product = giftProduct(c) || {};

    // Image: custom, or per-campaign gift image, with fallback to custom.
    var src = '';
    if (popup.imageSource === 'custom') {
      src = popup.customImage || '';
    } else {
      src = giftImages[c.discountId] || popup.customImage || '';
    }
    if (src && popupImage) {
      popupImage.src = src;
      popupImage.alt = product.title || '';
      popupImage.hidden = false;
      popupEl.classList.remove('sg-fg-popup--no-image');
    } else {
      if (popupImage) { popupImage.hidden = true; popupImage.removeAttribute('src'); }
      popupEl.classList.add('sg-fg-popup--no-image');
    }

    // Eyebrow: setting, else gift product name.
    var eyebrow = (popup.eyebrow && popup.eyebrow.replace(/^\s+|\s+$/g, '')) || product.title || '';
    if (eyebrow) {
      popupEyebrow.textContent = eyebrow;
      popupEyebrow.hidden = false;
    } else {
      popupEyebrow.hidden = true;
    }

    popupHeading.textContent = popup.heading || 'Unlock a special gift!';

    // Optional slashed gift value (no variant chosen yet → the gift product's price).
    if (popupValue) {
      var giftPrice = giftPrices[c.discountId];
      if (showValue && giftPrice != null) {
        popupValue.innerHTML = valueMarkup(giftPrice);
        popupValue.hidden = false;
      } else {
        popupValue.innerHTML = '';
        popupValue.hidden = true;
      }
    }

    // Message: replace [amount] with the formatted remaining amount.
    var amount = formatMoneySafe(entry.remainingCents);
    var msg = String(popup.message || '').split('[amount]').join(amount);
    popupMessage.innerHTML = msg;

    // CTA: link → navigate (no dismiss hook); blank → dismiss.
    popupCta.textContent = popup.button || 'Continue shopping';
    if (popup.buttonLink) {
      popupCta.setAttribute('href', popup.buttonLink);
      popupCta.removeAttribute('data-sg-fg-popup-dismiss');
    } else {
      popupCta.setAttribute('href', '#');
      popupCta.setAttribute('data-sg-fg-popup-dismiss', '');
    }

    // Countdown: drive the <sg-countdown> element via its `end` attribute (epoch ms).
    if (popupTimer) {
      var endsMs = c.endsAt ? Date.parse(c.endsAt) : NaN;
      if (popup.showTimer === true && !isNaN(endsMs) && endsMs > Date.now()) {
        popupTimer.setAttribute('end', String(endsMs));
        popupTimer.hidden = false;
      } else {
        popupTimer.removeAttribute('end');
        popupTimer.hidden = true;
      }
    }
  }

  function openPopup(entry) {
    if (!popupEls()) return;
    populatePopup(entry);
    popupEl.hidden = false;
    popupEl.setAttribute('aria-hidden', 'false');
    void popupEl.offsetWidth;   // force reflow so the transition runs
    popupEl.classList.add('is-open');
    document.body.classList.add('open-cc');
    popupOpen = true;
  }

  function closePopup() {
    if (!popupEl || !popupOpen) return;
    popupEl.classList.remove('is-open');
    popupEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('open-cc');
    popupOpen = false;
    if (popupTimer) popupTimer.removeAttribute('end');   // stops the <sg-countdown> tick
    var el = popupEl;
    setTimeout(function () { if (!popupOpen) el.hidden = true; }, 300);
  }

  var POPUP_SEEN_KEY = 'sgfg:popup:seen';

  function popupSeen() {
    try { return sessionStorage.getItem(POPUP_SEEN_KEY) === '1'; } catch (e) { return false; }
  }
  function setPopupSeen() {
    return;
    try { sessionStorage.setItem(POPUP_SEEN_KEY, '1'); } catch (e) {}
  }

  // Don't stack the popup over the variant-picker drawer.
  function variantDrawerActive() {
    var d = document.getElementById('Free-Gift-Drawer');
    return !!(d && d.classList.contains('active'));
  }

  // True inside the theme editor (Shopify.designMode). On its own this only detects the editor;
  // the always-on preview behavior is opt-in via the `alwaysShowInEditor` popup setting (see the
  // `editor` flag in maybeShowPopup). Never true on the live storefront.
  function inThemeEditor() {
    return !!(window.Shopify && Shopify.designMode);
  }

  // True when the shopper is on the cart page path (trailing-slash tolerant). Used to suppress the
  // popup there so it doesn't stack with the cart-page banner section.
  function onCartPage() {
    var cartPath = ((window.theme && theme.routes && theme.routes.cart_url) || '/cart').replace(/\/$/, '');
    var here = (window.location.pathname || '').replace(/\/$/, '');
    return here === cartPath;
  }

  // A synthetic "approaching" entry for the editor preview: the first campaign with a usable
  // gift product, shown at the configured "$ away" amount. Lets the popup render with real gift
  // data even when the preview cart is empty.
  function previewApproaching() {
    var awayCents = (Number(popup.thresholdAway) || 0) * 100 * currencyRate();
    for (var i = 0; i < campaigns.length; i++) {
      var c = campaigns[i];
      var product = c ? giftProduct(c) : null;
      if (product && product.handle) {
        return [{ campaign: c, remainingCents: awayCents }];
      }
    }
    return [];
  }

  function maybeShowPopup(approaching, byAdd) {
    if (!popup || popup.enabled !== true) return;

    // "Editor preview mode": inside the theme editor AND the merchant opted in via the "Always show
    // in theme editor" setting. This single flag drives both the forced preview (previewApproaching)
    // and the gating bypass below, so with the setting off the popup behaves exactly like live.
    var editor = inThemeEditor() && popup.alwaysShowInEditor === true;

    // Don't stack with the cart-page banner: suppress the popup on /cart (live storefront only;
    // still previewable in the theme editor).
    if (!editor && onCartPage()) {
      if (popupOpen) closePopup();
      return;
    }

    if (!approaching || !approaching.length) {
      if (editor) {
        approaching = previewApproaching();   // force a preview in the theme editor
        if (!approaching.length) return;
      } else {
        if (popupOpen) closePopup();          // left the band: qualified or dropped away
        return;
      }
    }
    if (!editor && variantDrawerActive()) return;

    // Smallest remaining gap wins.
    var active = approaching[0];
    for (var i = 1; i < approaching.length; i++) {
      if (approaching[i].remainingCents < active.remainingCents) active = approaching[i];
    }

    if (popupOpen) { populatePopup(active); return; }   // keep the amount fresh

    // In editor preview mode, skip the frequency/session gating so it shows on every preview.
    if (!editor) {
      if (popup.frequency === 'perAdd') {
        if (!byAdd) return;            // only (re)show on an actual add-to-cart
      } else {
        if (popupSeen()) return;       // 'once': already shown this session
        setPopupSeen();
      }
    }
    openPopup(active);
  }

  // --- wire up ---------------------------------------------------------------

  document.addEventListener('cart:item-added', onCartEvent);
  document.addEventListener('line-item:change:end', onCartEvent);

  // Flag adds so the popup's "every add" frequency mode can react (read in reconcile).
  document.addEventListener('cart:item-added', function () { pendingAdd = true; });

  // Dismiss the popup: backdrop, close button, or the CTA when it has no link.
  document.addEventListener('click', function (e) {
    var t = (e.target && e.target.closest) ? e.target.closest('[data-sg-fg-popup-dismiss]') : null;
    if (!t) return;
    e.preventDefault();
    closePopup();
  });
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && popupOpen) closePopup();
  });


  // Prompt button → open the variant modal (delegated; prompt rows are re-rendered).
  document.addEventListener('click', function (e) {
    var btn = (e.target && e.target.closest) ? e.target.closest('[data-sg-fg-choose]') : null;
    if (!btn) return;
    e.preventDefault();
    openModal(btn.getAttribute('data-handle'), btn.getAttribute('data-discount-id'));
  });

  // Confirm button (static in the modal shell).
  if (modalEls()) confirmBtn.addEventListener('click', onConfirm);

  // Keep the modal's price + image in sync as the shopper changes variant options.
  // Delegated on the content container, which survives across modal opens (its innerHTML is swapped).
  if (drawerContent) {
    drawerContent.addEventListener('change', function () {
      updateModalValue();   // no-op unless the merchant enabled the slashed value
      updateModalImage();
      updateModalLabels();
    });
  }

  // Initial pass — deferred a tick so Cart/CartDrawer have subscribed to cart:refresh.
  if (document.readyState === 'complete') {
    setTimeout(reconcile, 0);
  } else {
    window.addEventListener('load', function () { setTimeout(reconcile, 0); });
  }
})();
